import { ctaCreateId, dateId, excelDownloadId, searchId, segmentDisplayId } from "@/assets/googleTrackIds";
import { FiltersButton } from "@/components/Dataheader/FiltersButton";
import { FiltersDrawer } from "@/components/Dataheader/FiltersDrawer";
import { invoiceStatus, uploadV2Apis } from "@payping/api";
import { Add, FiEdit3, PhRCoins, PhRDownloadSimple, PhRSquaresFour } from "@payping/icons";
import { dateFormats, toUtc, utcNow } from "@payping/utils/dateUtils";
import { submitQueryParams } from "@payping/utils/query-string";
import {
  Button,
  DataHeader,
  Flex,
  Input,
  LegacyDatePicker,
  LegacyForm,
  Segmented,
  Select,
  Typography,
  useMessagedAsyncFn,
  usePaypingContext,
} from "@payping/web-components";
import qs from "query-string";
import React, { FC, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppTranslation } from "@/utils";
import {
  ListBoolQueryParamsType,
  ListNumberQueryParamsType,
  ListStringQueryParamsType,
} from "@/pages/Shop/Invoice/types";
import { USER_CLAIMS_LIST, useUserClaim } from "@/utils/useUserClaims";
import { DashboardRoutes } from "@/router";
import { InoviceStatusEnum } from "@payping/api/app/api/uploadV2Types";
import { useImmerAtom } from "jotai-immer";
import { coreAtom } from "@/core/state/root";

interface Props {
  retry: () => void;
  loading: boolean;
  setSelectedRowKeys: React.Dispatch<React.SetStateAction<string[]>>;
}

enum SegmentTypes {
  All = -1,
  Pishnevis = 0,
  PardakhtShodeh = 2,
}

export const ListFilters: FC<Props> = (props) => {
  const { retry, loading, setSelectedRowKeys } = props;
  const [, setCoreState] = useImmerAtom(coreAtom);

  const { t } = useAppTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [form] = LegacyForm.useForm();
  const { breakpoints } = usePaypingContext();
  const { lg, xl, xxl } = breakpoints;
  const [openDrawer, setOpenDrawer] = useState(false);
  const parsedSearch = qs.parse(location.search);
  const { searchText, searchDateFrom, searchDateTo }: ListStringQueryParamsType = qs.parse(location.search);
  const { status }: ListNumberQueryParamsType = qs.parse(location.search, {
    parseNumbers: true,
  });
  const { isArchived }: ListBoolQueryParamsType = qs.parse(location.search, { parseBooleans: true });
  const backFormat = dateFormats.standard.utcDateTime;

  const { hasPermission } = useUserClaim([USER_CLAIMS_LIST.invoiceService]);

  useEffect(() => {
    form.setFieldsValue({
      search: searchText,
      isArchived: Boolean(isArchived),
      dateValue: [
        toUtc({ date: searchDateFrom, dateFormat: backFormat, toFormat: backFormat }).object,
        toUtc({ date: searchDateTo, dateFormat: backFormat, toFormat: backFormat }).object,
      ],
      status,
    });
  }, [searchDateFrom, searchDateTo, searchText, isArchived, status, form, backFormat]);

  const [{ loading: loadingExcel }, generateExcel] = useMessagedAsyncFn(
    async (newWindow: Window | null) => {
      if (!loadingExcel) {
        const path = await uploadV2Apis.invoicesExportExcelWithModelPost({
          isArchived: isArchived || false,
          searchDateFrom,
          searchDateTo,
          searchTerm: searchText,
          status: typeof status === "number" ? (status as unknown as InoviceStatusEnum) : InoviceStatusEnum.All,
        });
        newWindow?.location?.assign?.(path);
      }
    },
    [isArchived, searchDateFrom, searchDateTo, searchText, status],
    undefined,
    { useProgress: true },
  );

  const onReset = (): void => {
    navigate({ pathname: location.pathname });
    form.resetFields();
    setOpenDrawer(false);
    setSelectedRowKeys([]);
  };

  const mobileFiltersBadgeCount = !lg
    ? {
        searchText,
        searchDateTo,
      }
    : undefined;

  return (
    <LegacyForm
      onFinish={(values): void => {
        const date = values.dateValue || [undefined, undefined];
        const [from, to] = [date[0]?.format(backFormat), date[1]?.format(backFormat)];
        setSelectedRowKeys([]);

        const newValues = {
          ...parsedSearch,
          isArchived: values.isArchived,
          searchText: values.search,
          status: values.status,
          searchDateFrom: from || undefined,
          searchDateTo: to || undefined,
        };
        submitQueryParams({
          navigate,
          newValues,
          parsedSearch,
          pathname: location.pathname,
          retry,
        });

        setOpenDrawer(false);
      }}
      form={form}
    >
      <DataHeader
        title={<Typography.Title level={4}>{t("invoice.title")}</Typography.Title>}
        primaryAction={
          <Button
            icon={<Add width={14} height={14} />}
            key={0}
            type={"primary"}
            onClick={() => {
              if (hasPermission) {
                navigate({ pathname: DashboardRoutes.myShop.invoice.create.url }, { state: location.state });
              } else {
                setCoreState((draft) => {
                  // eslint-disable-next-line no-param-reassign
                  draft.upgradeUserPanel.toPro.service = "invoice";
                  // eslint-disable-next-line no-param-reassign
                  draft.upgradeUserPanel.toPro.showModal = true;
                });
              }
            }}
            data-google-track={ctaCreateId}
          >
            {t("add.button", { subject: t("invoice.title") })}
          </Button>
        }
        filters={
          !hasPermission
            ? undefined
            : {
                segmentsButton: (
                  <Segmented
                    key={5}
                    options={[
                      {
                        value: SegmentTypes.All,
                        icon: <PhRSquaresFour width={16} />,
                        label: t("invoice.create.base.invoiceStatus.all"),
                      },
                      {
                        value: SegmentTypes.Pishnevis,
                        icon: <FiEdit3 strokeWidth={1.5} width={16} />,
                        label: t("invoice.create.base.invoiceStatus.draft"),
                      },
                      {
                        value: SegmentTypes.PardakhtShodeh,
                        icon: <PhRCoins width={16} />,
                        label: t("invoice.create.base.invoiceStatus.paid"),
                      },
                    ]}
                    onChange={(value) => {
                      if (value === SegmentTypes.All) return onReset();

                      const newValues = {
                        status: value,
                      };
                      return submitQueryParams({
                        navigate,
                        newValues,
                        parsedSearch,
                        pathname: location.pathname,
                        retry,
                      });
                    }}
                    data-google-track={segmentDisplayId}
                  />
                ),
                datepickerInput: xl && (
                  <LegacyForm.Item key={4} name={"dateValue"} noStyle>
                    <LegacyDatePicker.RangePicker
                      onChange={() => form.submit()}
                      disabledDate={(date) => date.isAfter(utcNow().object)}
                      className="tw:w-full"
                      data-google-track={dateId}
                    />
                  </LegacyForm.Item>
                ),
                searchbarInput: xxl && (
                  <LegacyForm.Item key={5} name={"search"} noStyle>
                    <Input.Search
                      style={xl ? { width: 300 } : {}}
                      allowClear
                      onSearch={() => form.submit()}
                      placeholder={t("invoice.placeholder")}
                      data-google-track={searchId}
                    />
                  </LegacyForm.Item>
                ),
                filtersButton: (
                  <>
                    <FiltersButton
                      onClick={() => setOpenDrawer(true)}
                      params={{
                        isArchived,
                        status,
                        ...mobileFiltersBadgeCount,
                      }}
                    />

                    <FiltersDrawer
                      title={t("filter")}
                      onClose={() => setOpenDrawer(false)}
                      open={openDrawer}
                      onClickApplyFilters={() => form.submit()}
                      onClickResetFilters={() => onReset()}
                      loading={loading}
                    >
                      <Flex vertical gap={8}>
                        {/* < Title > */}
                        <Typography.Title level={5}>{t("invoice.transactionDetails")}</Typography.Title>
                        {/* </ Title > */}

                        {!xl ? (
                          <>
                            {/* < DatePicker > */}
                            <LegacyForm.Item label={t("date")} name={"dateValue"} layout={"vertical"}>
                              <LegacyDatePicker.RangePicker
                                disabledDate={(date) => date.isAfter(utcNow().object)}
                                className="tw:w-full"
                                data-google-track={dateId}
                              />
                            </LegacyForm.Item>
                            {/* </ DatePicker > */}
                          </>
                        ) : null}

                        {/* < Search > */}
                        <LegacyForm.Item label={t("search.search")} name={"search"} layout={"vertical"}>
                          <Input allowClear placeholder={t("invoice.placeholder")} data-google-track={searchId} />
                        </LegacyForm.Item>
                        {/* </ Search > */}

                        {/* < isArchived > */}
                        <LegacyForm.Item label={t("type")} name={"isArchived"} layout={"vertical"}>
                          <Select
                            allowClear
                            className={"w-100"}
                            options={[
                              { label: t("archived"), value: true },
                              { label: t("notArchived"), value: false },
                            ]}
                          />
                        </LegacyForm.Item>
                        {/* </ isArchived > */}

                        {/* < Status > */}
                        <LegacyForm.Item
                          label={t("report.filters.paymentStatus.title")}
                          name="status"
                          layout={"vertical"}
                        >
                          <Select
                            allowClear
                            className={"w-100"}
                            options={[
                              { label: invoiceStatus[0].name, value: 0 },
                              { label: invoiceStatus[1].name, value: 1 },
                              { label: invoiceStatus[2].name, value: 2 },
                              { label: invoiceStatus[3].name, value: 3 },
                              { label: invoiceStatus[4].name, value: 4 },
                              { label: invoiceStatus[6].name, value: 6 },
                            ]}
                          />
                        </LegacyForm.Item>
                        {/* </ Status > */}
                      </Flex>
                    </FiltersDrawer>
                  </>
                ),
                downloadButton: (
                  <LegacyForm.Item key={2} noStyle>
                    <Button
                      className="w-100"
                      icon={<PhRDownloadSimple width={14} height={14} />}
                      onClick={() => {
                        const newWindow = window.open(undefined, "_blank");
                        generateExcel(newWindow);
                      }}
                      key={1}
                      data-google-track={excelDownloadId}
                    >
                      {t("downloadExcel")}
                    </Button>
                  </LegacyForm.Item>
                ),
              }
        }
      />
    </LegacyForm>
  );
};

export default ListFilters;
